const ACTIVITY_INVENTORY_MEDIA_DEFAULTS = {
	heroImage: null,
	heroImageUrl: "",
	logoImage: null,
	logoImageSmall: null
};

/**
 * Build the media fields stored in the lightweight activity inventory entry.
 * Full activity JSON can contain both a Drive resource reference and an
 * already-resolved URL. Cards need the latter before Drive has hydrated.
 */
export function getActivityInventoryMediaFields(activity = {}, options = {}) {
	const { onlyPresent = false } = options;
	const result = {};

	Object.keys(ACTIVITY_INVENTORY_MEDIA_DEFAULTS).forEach((field) => {
		if (onlyPresent && !Object.prototype.hasOwnProperty.call(activity, field)) {
			return;
		}

		const value = activity[field];
		result[field] = value === null || value === undefined
			? ACTIVITY_INVENTORY_MEDIA_DEFAULTS[field]
			: value;
	});

	return result;
}

// Resolve from the supplied resource manifest, without requiring the Drive UI to load.
export function getDriveResourceUrl(resource, assetUrls = {}) {
	if (!resource || typeof resource.path !== "string" || !resource.path) return "";
	const isPrivate = Object.prototype.hasOwnProperty.call(resource, "private")
		? !!resource.private : /^\/?private\//.test(resource.path);
	const prefix = assetUrls[isPrivate ? "private" : "public"];
	if (!prefix) return "";
	const path = resource.path.replace(/^\/?(?:public|private)\//, "").replace(/^\/+/, "");
	const encodedPath = path.split("/").map(part => {
		for (let attempt = 0; attempt < 4; attempt += 1) {
			try {
				const decoded = decodeURIComponent(part);
				if (decoded === part) break;
				part = decoded;
			} catch (_err) { break; }
		}
		return encodeURIComponent(part);
	}).join("/");
	const revision = resource.revision === null || resource.revision === undefined
		? "" : `?v=${encodeURIComponent(resource.revision)}`;
	return `${prefix.replace(/\/+$/, "")}/${encodedPath}${revision}`;
}

export function getActivityHeroMediaFields(activity = {}, { runtimeActivity = null, assetUrls = {}, getDriveResource = () => null } = {}) {
	// Partial metadata saves must not erase an existing image.
	if (!Object.prototype.hasOwnProperty.call(activity, "heroImage")) {
		return Object.prototype.hasOwnProperty.call(activity, "heroImageUrl")
			? { heroImageUrl: activity.heroImageUrl || "" } : {};
	}
	const heroImage = activity.heroImage;
	let heroImageUrl = "";
	if (typeof heroImage === "string") {
		heroImageUrl = heroImage;
	} else if (heroImage && heroImage.type === "resource/ref") {
		const resources = [
			...((runtimeActivity && runtimeActivity.externalResources) || []),
			...(activity.externalResources || [])
		];
		const manifestResource = resources.find(entry => entry && entry.id === heroImage.refId && entry.path)
			|| resources.find(entry => entry && entry.id === heroImage.refId);
		// Direct saves (such as cloning) may skip runtime compilation but already have Drive loaded.
		const resource = manifestResource && (manifestResource.path ? manifestResource : getDriveResource(manifestResource.guid));
		// A partial save may have only an already-resolved URL, without resource details.
		heroImageUrl = resource ? getDriveResourceUrl(resource, assetUrls) : (activity.heroImageUrl || "");
	}
	return { heroImage: heroImage || null, heroImageUrl };
}
